import React from 'react';
import { List, ListItem, ListItemButton, ListItemIcon, ListItemText, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';

// ========================= STYLED COMPONENTS =========================

// Styled List container
const StyledList = styled(List)(({ theme }) => ({
  flexGrow: 1,
  paddingTop: theme.spacing(0.5),
}));

// Styled ListItemButton with dynamic props
interface StyledListItemButtonProps {
  expanded: boolean;
  isSelected: boolean;
}

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'expanded' && prop !== 'isSelected',
})<StyledListItemButtonProps>(({ theme, expanded, isSelected }) => ({
  marginLeft: theme.spacing(0.5),
  marginRight: theme.spacing(0.5),
  borderRadius: theme.spacing(1.5),
  marginBottom: theme.spacing(0.25),
  backgroundColor: isSelected ? 'rgba(255,255,255,0.2)' : 'transparent',
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
  '&.Mui-selected': {
    backgroundColor: 'rgba(255,255,255,0.2)',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.3)',
    },
  },
  minHeight: 44,
  justifyContent: expanded ? 'flex-start' : 'center',
}));

// Styled ListItemIcon with dynamic props
interface StyledListItemIconProps {
  expanded: boolean;
}

const StyledListItemIcon = styled(ListItemIcon, {
  shouldForwardProp: (prop) => prop !== 'expanded',
})<StyledListItemIconProps>(({ theme, expanded }) => ({
  color: 'white',
  minWidth: expanded ? 40 : 'auto',
  marginRight: expanded ? theme.spacing(2) : 0,
}));

// Styled ListItemText with dynamic props
interface StyledListItemTextProps {
  isSelected: boolean;
}

const StyledListItemText = styled(ListItemText, {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})<StyledListItemTextProps>(({ isSelected }) => ({
  '& .MuiListItemText-primary': {
    fontSize: '0.875rem',
    fontWeight: isSelected ? 'bold' : 'normal',
  },
}));

// ========================= INTERFACES =========================
interface MenuItem {
  text: string;
  path: string;
  icon: React.ReactNode;
  roles: string[];
  isExternal?: boolean;
  openInNewTab?: boolean;
}

interface NavigationMenuProps {
  items: MenuItem[];
  currentPath: string;
  onItemClick: (path: string, isExternal?: boolean, openInNewTab?: boolean) => void;
  expanded: boolean;
  isAdmin: boolean;
}

// ========================= COMPONENT =========================
const NavigationMenu: React.FC<NavigationMenuProps> = ({ items, currentPath, onItemClick, expanded, isAdmin }) => {
  const isItemSelected = (itemPath: string) => {
    if (itemPath === '/admin-panel/overview' || itemPath === '/admin-panel/principal-dashboard') {
      return currentPath === itemPath || currentPath === '/admin-panel';
    }
    return currentPath === itemPath;
  };
  const { t } = useTranslation();

  const disabled = (item: MenuItem): boolean => {
    if (item.text === t('comprehensiveOverview')) return true;
    if (item.text === t('statistics') && !isAdmin) return true;
    return false;
  };

  return (
    <StyledList>
      {items.map((item) => {
        const isSelected = isItemSelected(item.path);

        return (
          <ListItem key={item.text} disablePadding>
            <StyledListItemButton
              onClick={() => onItemClick(item.path, item.isExternal, item.openInNewTab)}
              selected={isSelected}
              expanded={expanded}
              isSelected={isSelected}
              disabled={disabled(item)} // Disable items based on conditions
            >
              <StyledListItemIcon expanded={expanded}>{item.icon}</StyledListItemIcon>
              {expanded && <StyledListItemText primary={item.text} isSelected={isSelected} />}
            </StyledListItemButton>
          </ListItem>
        );
      })}
    </StyledList>
  );
};

export default NavigationMenu;
